import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { useAuth } from "../../context/AuthContext";
import {
  calculateRudraksha,
  getMyRudrakshaReport,
  getMyRudrakshaReports,
  getRudrakshaOptions,
} from "../../api/rudrakshaApi";
import { checkDob, DOB_ERROR, formatDob, maskDob } from "../../utils/dob";
import "./RudrakshaCalculator.css";

const SEO = {
  title: "Rudraksha Calculator | Find Your Rudraksha by Date of Birth – Vaidik Vidhya™",
  description:
    "Enter your date of birth and goal to find the rudraksha traditionally suggested for you, from your Mulank, Bhagyank, Moon sign and purpose. Free Rudraksha calculator by Vaidik Vidhya™.",
};

const LAYER_LABEL = {
  numbers: "Your Mulank & Bhagyank",
  goal: "Your goal",
  rashi: "Your Moon sign",
};

const MODE_NOTE = {
  common: "These numbers are friendly to both your Mulank and your Bhagyank.",
  balanced:
    "Your Mulank and Bhagyank have no friendly number in common, so we looked for numbers that neither of them dislikes and at least one of them favours.",
  own: "Your Mulank and Bhagyank clash completely, so we suggest the rudraksha of each of your own numbers.",
};

const mukhiLink = (mukhi) => `/search?q=${encodeURIComponent(`${mukhi} mukhi`)}`;
const mukhiList = (items) => items.map((r) => r.mukhi).join(" / ");

function NumberCard({ title, value, planet }) {
  return (
    <div className="rc-num">
      <small>{title}</small>
      <strong>{value}</strong>
      <span>
        {planet.en} <em lang="hi">({planet.hi})</em>
      </span>
    </div>
  );
}

function TopPicks({ picks }) {
  return (
    <div className="rc-picks">
      {picks.map((p, i) => (
        <article key={p.mukhi} className={`rc-pick ${i === 0 ? "is-best" : ""}`}>
          {i === 0 && <span className="rc-pick-flag">Best match</span>}
          <h4>{p.label}</h4>
          <p className="rc-pick-hi" lang="hi">
            {p.labelHi}
          </p>
          {p.planet && (
            <span className="rc-chip">
              {p.planet.en} <em lang="hi">({p.planet.hi})</em>
            </span>
          )}
          <p className="rc-pick-agree">{p.strength > 1 ? `${p.strength} factors agree` : "Suggested by 1 factor"}</p>
          <ul>
            {p.matches.map((m) => (
              <li key={m.layer}>
                <strong>{LAYER_LABEL[m.layer]}:</strong> {m.detail}
              </li>
            ))}
          </ul>
          <Link className="rc-pick-cta" to={mukhiLink(p.mukhi)}>
            View {p.label} Rudraksha →
          </Link>
        </article>
      ))}
    </div>
  );
}

function RashiSection({ rashi, byRashi, onAddTime }) {
  if (rashi.status === "ambiguous") {
    const [a, b] = rashi.candidates;
    return (
      <div className="rc-note is-info">
        <strong>Your Moon sign needs your time of birth.</strong>
        <p>
          On your birth date the Moon moves from {a.en} ({a.hi}) to {b.en} ({b.hi}) at about {rashi.changeAt} IST, so the date alone does not decide your rashi.
          Add your time of birth to include your Moon sign in the suggestion.
        </p>
        <button type="button" className="rc-linkbtn" onClick={onAddTime}>
          Add time of birth
        </button>
      </div>
    );
  }
  return (
    <>
      <div className="rc-facts">
        <div>
          <small>Moon sign (Rashi)</small>
          <strong>
            {rashi.sign.en} <span lang="hi">({rashi.sign.hi})</span>
          </strong>
          {rashi.degree !== undefined && <span>{rashi.degree}° in the sign</span>}
        </div>
        <div>
          <small>Ruling planet</small>
          <strong>
            {rashi.sign.lord.en} <span lang="hi">({rashi.sign.lord.hi})</span>
          </strong>
        </div>
        <div>
          <small>Suggested rudraksha</small>
          <strong>{mukhiList(byRashi.rudraksha)} Mukhi</strong>
        </div>
      </div>
      {rashi.status === "dateOnly" && (
        <p className="rc-hint">The Moon stays in {rashi.sign.en} for the whole of your birth date, so your time of birth is not needed.</p>
      )}
      {rashi.nearBoundary && rashi.neighbour && (
        <div className="rc-note is-warn">
          Your time of birth is close to the moment the Moon changes sign ({rashi.sign.en} / {rashi.neighbour.en}). Please confirm your time against your
          Kundli.
        </div>
      )}
    </>
  );
}

function Result({ result, onAddTime }) {
  const { profile, friendly, byNumber, byGoal, rashi, byRashi, topPicks } = result;
  return (
    <div className="rc-result">
      <h3 className="rc-greet">
        Namaste, {result.input.name}
        <small>Your suggested Rudraksha</small>
      </h3>
      <TopPicks picks={topPicks} />

      <h3 className="rc-h">Your numbers</h3>
      <div className="rc-nums">
        <NumberCard title="Mulank (day of birth)" value={profile.mulank} planet={profile.mulankPlanet} />
        <NumberCard title="Bhagyank (full date of birth)" value={profile.bhagyank} planet={profile.bhagyankPlanet} />
      </div>

      <h3 className="rc-h">Rudraksha for your numbers</h3>
      <p className="rc-hint">{MODE_NOTE[friendly.mode]}</p>
      <div className="rc-table" role="table" aria-label="Suggested numbers and their rudraksha">
        <div className="rc-row rc-row-head" role="row">
          <span role="columnheader">Number</span>
          <span role="columnheader">Planet</span>
          <span role="columnheader">Rudraksha</span>
          <span role="columnheader">Why</span>
        </div>
        {byNumber.map((n) => (
          <div className="rc-row" role="row" key={n.number}>
            <span role="cell" className="rc-cell-num">
              {n.number}
            </span>
            <span role="cell">
              {n.planet.en} <em lang="hi">({n.planet.hi})</em>
            </span>
            <span role="cell">
              {n.rudraksha.map((r, i) => (
                <React.Fragment key={r.mukhi}>
                  {i > 0 && " / "}
                  <Link to={mukhiLink(r.mukhi)}>{r.label}</Link>
                </React.Fragment>
              ))}
            </span>
            <span role="cell" className="rc-why">
              {n.reason}
            </span>
          </div>
        ))}
      </div>

      {byGoal && (
        <>
          <h3 className="rc-h">For your goal</h3>
          <p className="rc-goal-title">
            {byGoal.title} <span lang="hi">({byGoal.titleHi})</span>
          </p>
          <div className="rc-chips">
            {byGoal.rudraksha.map((r) => (
              <Link key={r.mukhi} className="rc-chip is-link" to={mukhiLink(r.mukhi)}>
                {r.label} <em lang="hi">({r.labelHi})</em>
              </Link>
            ))}
          </div>
        </>
      )}

      <h3 className="rc-h">Your Moon sign</h3>
      <RashiSection rashi={rashi} byRashi={byRashi} onAddTime={onAddTime} />

      <p className="rc-disclaimer">{result.disclaimer}</p>
      <p className="rc-consult">
        Want a reading that looks at your whole Kundli? <Link to="/consultation">Talk to an astrologer</Link>
      </p>
    </div>
  );
}

export default function RudrakshaCalculator() {
  const { accessToken, isLoggedIn, initializing, user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("calculate");
  const [concerns, setConcerns] = useState([]);

  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [time, setTime] = useState("");
  const [place, setPlace] = useState("");
  const [concern, setConcern] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    getRudrakshaOptions().then((r) => {
      if (r.ok && r.success) setConcerns(r.data.concerns || []);
    });
  }, []);

  // Pre-fill the name once with the account name; never overwrite what they typed.
  useEffect(() => {
    if (user?.name) setName((current) => current || user.name);
  }, [user]);

  useEffect(() => {
    if (tab !== "history" || !accessToken) return;
    getMyRudrakshaReports(accessToken).then((r) => setReports(r.ok && r.success ? r.data : []));
  }, [tab, accessToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2) {
      setError("Please enter your name.");
      return;
    }
    const dobText = checkDob(dob);
    if (!dobText) {
      setError(dobText === "" ? "Please enter your date of birth." : DOB_ERROR);
      return;
    }
    setLoading(true);
    const r = await calculateRudraksha(
      {
        name: name.trim(),
        dob: dobText,
        ...(time ? { timeOfBirth: time } : {}),
        ...(place.trim() ? { placeOfBirth: place.trim() } : {}),
        ...(concern ? { concern } : {}),
      },
      accessToken,
    );
    setLoading(false);
    if (r.ok && r.success) {
      setResult(r.data);
      setTimeout(() => document.getElementById("rc-result-anchor")?.scrollIntoView({ behavior: "smooth" }), 50);
    } else {
      setResult(null);
      setError(r.message || "Could not calculate your rudraksha. Please try again.");
    }
  };

  const openReport = async (id) => {
    const r = await getMyRudrakshaReport(id, accessToken);
    if (!(r.ok && r.success)) return;
    const { input } = r.data.result;
    setName(input.name);
    setDob(formatDob(input.dob));
    setTime(input.timeOfBirth || "");
    setPlace(input.placeOfBirth || "");
    setConcern(input.concern || "");
    setResult({ reportId: r.data.id, ...r.data.result });
    setError("");
    setTab("calculate");
    setTimeout(() => document.getElementById("rc-result-anchor")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const addTime = () => {
    const field = document.getElementById("rc-time");
    field?.scrollIntoView({ behavior: "smooth", block: "center" });
    field?.focus({ preventScroll: true });
  };

  let body;
  if (initializing) {
    body = <p className="rc-loading">Checking your sign-in...</p>;
  } else if (!isLoggedIn) {
    body = (
      <div className="rc-signin">
        <h3>Sign in to use the Rudraksha calculator</h3>
        <p>The calculator is free for registered customers. It takes a minute to sign in or create an account.</p>
        <button
          type="button"
          className="rc-submit"
          onClick={() => navigate("/login", { state: { redirectTo: "/rudraksha-calculator" } })}
        >
          Sign in to continue
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <div className="rc-tabs" role="tablist">
          {[
            ["calculate", "Calculator"],
            ["history", "My reports"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`rc-tab ${tab === key ? "is-active" : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "calculate" && (
          <>
            <form className="rc-form" onSubmit={submit} noValidate>
              <label>
                Name *
                <input
                  type="text"
                  autoComplete="name"
                  maxLength={100}
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label>
                Date of birth *
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="bday"
                  placeholder="DD/MM/YYYY"
                  maxLength={10}
                  value={dob}
                  onChange={(e) => setDob(maskDob(e.target.value))}
                />
              </label>
              <label>
                Time of birth <small>(optional)</small>
                <input id="rc-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                <span className="rc-field-hint">Gives your exact Moon sign. Indian Standard Time.</span>
              </label>
              <label>
                Place of birth <small>(optional)</small>
                <input
                  type="text"
                  autoComplete="off"
                  maxLength={150}
                  placeholder="City, State"
                  value={place}
                  onChange={(e) => setPlace(e.target.value)}
                />
                <span className="rc-field-hint">Kept for your reference; it does not change the result.</span>
              </label>
              <label className="rc-span">
                Your goal <small>(optional)</small>
                <select value={concern} onChange={(e) => setConcern(e.target.value)}>
                  <option value="">No specific goal</option>
                  {concerns.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.title} ({c.titleHi})
                    </option>
                  ))}
                </select>
              </label>
              <button className="rc-submit rc-span" type="submit" disabled={loading}>
                {loading ? "Calculating..." : "Find my Rudraksha"}
              </button>
            </form>
            {error && (
              <div className="rc-error" role="alert">
                {error}
              </div>
            )}
            <div id="rc-result-anchor" />
            {result && <Result result={result} onAddTime={addTime} />}
          </>
        )}

        {tab === "history" && (
          <div className="rc-history">
            {reports === null && <p className="rc-loading">Loading...</p>}
            {reports && reports.length === 0 && <p className="rc-hint">You have not used the calculator yet.</p>}
            {reports &&
              reports.map((r) => (
                <button key={r.id} type="button" className="rc-history-row" onClick={() => openReport(r.id)}>
                  <strong>{r.name}</strong>
                  <span>DOB {formatDob(r.dob)}</span>
                  <span>
                    Mulank {r.mulank} / Bhagyank {r.bhagyank}
                  </span>
                  <span className="rc-chip">{r.picks.split(",").join(", ")} Mukhi</span>
                  <small>{new Date(r.createdAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}</small>
                </button>
              ))}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <link rel="canonical" href={`${window.location.origin}/rudraksha-calculator`} />
      </Helmet>
      <Header />
      <main className="rc">
        <section className="rc-hero">
          <span className="rc-eyebrow">Vaidik Vidhya™ · Rudraksha Guidance</span>
          <h1>Rudraksha Calculator</h1>
          <p className="rc-hero-hi" lang="hi">
            रुद्राक्ष कैलकुलेटर
          </p>
          <p className="rc-lead">
            Enter your date of birth and, if you like, your goal. We work out your Mulank and Bhagyank, find the numbers friendly to both, and suggest the
            rudraksha traditionally linked to them.
          </p>
        </section>
        <div className="rc-shell">{body}</div>
      </main>
      <Footer />
    </>
  );
}
